import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { listInquiries, patchInquiry } from '../api/educationInquiries';
import { useDebounce } from '../hooks';
import {
  INQUIRY_STATUSES,
  INQUIRY_STATUS_MAP,
  ORDERING_OPTIONS,
} from '../types/educationInquiry';
import type {
  EducationInquiry,
  InquiryFilters,
  InquiryStatus,
  InquiryOrdering,
  InquiryPatchPayload,
} from '../types/educationInquiry';
import {
  generateEducationInquiryEmail,
  getEducationUnitPrice,
  DEFAULT_SUBJECT,
} from '../utils/educationEmailTemplates';
import styles from './EducationInquiries.module.css';

const PAGE_SIZE = 50;

function safeRelative(iso: string | null | undefined): string {
  if (!iso) return '-';
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

function safeFullDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  try {
    return format(parseISO(iso), 'MMM d, yyyy h:mm a');
  } catch {
    return iso;
  }
}

function toInputDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 16);
}

function fromInputDateTime(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function StatusBadge({ status }: { status: InquiryStatus }) {
  const meta = INQUIRY_STATUS_MAP[status];
  if (!meta) return <span className={styles.badge}>{status}</span>;
  return (
    <span
      className={styles.badge}
      style={{
        background: `${meta.color}26`,
        color: meta.color,
        border: `1px solid ${meta.color}55`,
      }}
    >
      {meta.label}
    </span>
  );
}

const COPY_ICON = (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CHECK_ICON = (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

function CopyEmailButton({ email }: { email: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (timerRef.current) clearTimeout(timerRef.current);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(email);
      } else {
        const ta = document.createElement('textarea');
        ta.value = email;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setState('copied');
    } catch {
      setState('failed');
    }
    timerRef.current = setTimeout(() => setState('idle'), 1400);
  };

  const cls = `${styles.copyIcon} ${state === 'copied' ? styles.copyIconOk : ''} ${state === 'failed' ? styles.copyIconErr : ''}`;

  return (
    <button
      type="button"
      className={cls}
      onClick={handleCopy}
      title={
        state === 'copied'
          ? 'Email copied!'
          : state === 'failed'
          ? 'Copy failed'
          : `Copy ${email}`
      }
      aria-label="Copy email to clipboard"
    >
      {state === 'copied' ? CHECK_ICON : COPY_ICON}
    </button>
  );
}

interface MarkContactedButtonProps {
  inquiry: EducationInquiry;
  onUpdated: (i: EducationInquiry) => void;
}

function MarkContactedButton({ inquiry, onUpdated }: MarkContactedButtonProps) {
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (marking) return;
    setMarking(true);
    setError(null);
    try {
      const updated = await patchInquiry(inquiry.id, { status: 'contacted' });
      onUpdated(updated);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed';
      setError(msg);
      setTimeout(() => setError(null), 2500);
    } finally {
      setMarking(false);
    }
  };

  return (
    <button
      type="button"
      className={`${styles.markContactedChip} ${error ? styles.markContactedErr : ''}`}
      onClick={handleClick}
      disabled={marking}
      title={error ? error : 'Mark as contacted'}
      aria-label="Mark as contacted"
    >
      {marking ? '…' : error ? '✕ failed' : '→ Contacted'}
    </button>
  );
}

/**
 * Best-effort clipboard write with a textarea fallback for non-secure
 * contexts. Returns true on success.
 */
async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

interface CopyChipButtonProps {
  label: string;
  primary?: boolean;
  getText: () => string;
  onCopied?: () => void;
}

function CopyChipButton({ label, primary, getText, onCopied }: CopyChipButtonProps) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleClick = async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const ok = await copyText(getText());
    setState(ok ? 'copied' : 'failed');
    if (ok && onCopied) onCopied();
    timerRef.current = setTimeout(() => setState('idle'), 1400);
  };

  const cls = [
    styles.copyChip,
    primary ? styles.copyChipPrimary : '',
    state === 'copied' ? styles.copyChipOk : '',
    state === 'failed' ? styles.copyChipErr : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type="button" className={cls} onClick={handleClick}>
      <span className={styles.copyChipIcon} aria-hidden="true">
        {state === 'copied' ? CHECK_ICON : COPY_ICON}
      </span>
      <span>
        {state === 'copied'
          ? 'Copied'
          : state === 'failed'
          ? 'Copy failed'
          : label}
      </span>
    </button>
  );
}

interface SuggestedResponseSectionProps {
  inquiry: EducationInquiry;
  onUpdated: (i: EducationInquiry) => void;
  /**
   * Optional staged quantity from the drawer's editable field. Used to
   * preview tier price and template paragraphs against an unsaved estimate.
   * `null` means "explicitly cleared"; falsy `undefined` falls back to the
   * saved value on the inquiry.
   */
  effectiveQuantity?: number | null;
}

function SuggestedResponseSection({
  inquiry,
  onUpdated,
  effectiveQuantity,
}: SuggestedResponseSectionProps) {
  const quantity =
    effectiveQuantity !== undefined ? effectiveQuantity : inquiry.estimated_quantity;
  const autoUnitPrice = useMemo(() => getEducationUnitPrice(quantity), [quantity]);

  const [subject, setSubject] = useState<string>(DEFAULT_SUBJECT);
  const [manualPriceInput, setManualPriceInput] = useState<string>('');
  const [includeTeacherDeviceOffer, setIncludeTeacherDeviceOffer] = useState<boolean>(
    quantity != null && quantity >= 20,
  );
  const [bodyEdited, setBodyEdited] = useState<boolean>(false);
  const [body, setBody] = useState<string>('');
  const [didCopy, setDidCopy] = useState<boolean>(false);
  const [marking, setMarking] = useState<boolean>(false);
  const [markError, setMarkError] = useState<string | null>(null);
  const [markedAt, setMarkedAt] = useState<Date | null>(null);

  const manualUnitPrice = useMemo(() => {
    const trimmed = manualPriceInput.trim();
    if (!trimmed) return undefined;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }, [manualPriceInput]);

  const generated = useMemo(
    () =>
      generateEducationInquiryEmail({
        inquiry,
        manualUnitPrice,
        includeTeacherDeviceOffer,
        subject,
        effectiveQuantity,
      }),
    [inquiry, manualUnitPrice, includeTeacherDeviceOffer, subject, effectiveQuantity],
  );

  // Reset subject + auto-edits when a different inquiry is loaded into the
  // drawer. We deliberately do NOT reset the teacher-device toggle on every
  // staged-quantity edit; that's handled by the dedicated effect below so
  // the user doesn't have to re-toggle it as they type.
  useEffect(() => {
    setSubject(DEFAULT_SUBJECT);
    setManualPriceInput('');
    setBodyEdited(false);
    setDidCopy(false);
    setMarkError(null);
    setMarkedAt(null);
  }, [inquiry.id]);

  // Recompute the teacher-device default when the effective quantity crosses
  // the 20+ threshold (either from a different inquiry being loaded or from
  // the staff estimate being typed).
  useEffect(() => {
    setIncludeTeacherDeviceOffer(quantity != null && quantity >= 20);
  }, [inquiry.id, quantity]);

  // Live regenerate the body whenever the controlled inputs change — but
  // only as long as the admin hasn't manually edited the textarea. Manual
  // edits are preserved until the next inquiry is loaded.
  useEffect(() => {
    if (!bodyEdited) {
      setBody(generated.body);
    }
  }, [generated.body, bodyEdited]);

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setBody(e.target.value);
    setBodyEdited(true);
  };

  const handleResetBody = () => {
    setBody(generated.body);
    setBodyEdited(false);
  };

  const handleMarkContacted = async () => {
    if (marking) return;
    setMarking(true);
    setMarkError(null);
    try {
      const updated = await patchInquiry(inquiry.id, { status: 'contacted' });
      onUpdated(updated);
      setMarkedAt(new Date());
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to update status';
      setMarkError(msg);
    } finally {
      setMarking(false);
    }
  };

  const fullEmail = `Subject: ${subject || DEFAULT_SUBJECT}\n\n${body}`;

  const showMarkPrompt = didCopy && inquiry.status === 'new' && markedAt == null;

  return (
    <section className={styles.drawerSection}>
      <div className={styles.crmHeader}>
        <h3 className={styles.drawerSectionTitle}>Suggested Response</h3>
        <span className={styles.fieldHint}>
          Tier price:{' '}
          {autoUnitPrice != null
            ? `$${autoUnitPrice}/unit`
            : quantity == null
            ? 'no quantity'
            : 'custom quote'}
        </span>
      </div>

      <div className={styles.fieldRow}>
        <div className={styles.field}>
          <label>Manual unit price override (USD)</label>
          <div className={styles.priceInputWrap}>
            <span className={styles.priceInputPrefix}>$</span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="1"
              placeholder={
                autoUnitPrice != null ? String(autoUnitPrice) : 'custom'
              }
              value={manualPriceInput}
              onChange={(e) => setManualPriceInput(e.target.value)}
              className={`${styles.input} ${styles.priceInput}`}
            />
          </div>
          <span className={styles.fieldHint}>
            Leave blank to use the tier-based price.
          </span>
        </div>

        <div className={styles.field}>
          <label>Teacher device</label>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={includeTeacherDeviceOffer}
              onChange={(e) => setIncludeTeacherDeviceOffer(e.target.checked)}
            />
            <span>Include teacher device offer paragraph</span>
          </label>
          <span className={styles.fieldHint}>
            Defaults to on when estimated quantity is 20+.
          </span>
        </div>
      </div>

      <div className={styles.field}>
        <label>Subject</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className={styles.input}
          placeholder={DEFAULT_SUBJECT}
        />
      </div>

      <div className={styles.field}>
        <div className={styles.bodyHeader}>
          <label>Body</label>
          {bodyEdited && (
            <button
              type="button"
              className={styles.linkButton}
              onClick={handleResetBody}
              title="Discard your edits and regenerate from the template"
            >
              Reset to template
            </button>
          )}
        </div>
        <textarea
          value={body}
          onChange={handleBodyChange}
          rows={18}
          className={`${styles.textarea} ${styles.bodyTextarea}`}
          spellCheck
        />
      </div>

      <div className={styles.copyRow}>
        <CopyChipButton
          label="Copy subject"
          getText={() => subject || DEFAULT_SUBJECT}
          onCopied={() => setDidCopy(true)}
        />
        <CopyChipButton
          label="Copy body"
          getText={() => body}
          onCopied={() => setDidCopy(true)}
        />
        <CopyChipButton
          label="Copy full email"
          primary
          getText={() => fullEmail}
          onCopied={() => setDidCopy(true)}
        />
      </div>

      {showMarkPrompt && (
        <div className={styles.markPrompt}>
          <span>Sent the email?</span>
          <button
            type="button"
            className={styles.markPromptBtn}
            onClick={handleMarkContacted}
            disabled={marking}
          >
            {marking ? 'Updating…' : 'Mark inquiry as contacted →'}
          </button>
          {markError && <span className={styles.markPromptErr}>{markError}</span>}
        </div>
      )}

      {markedAt && (
        <div className={styles.markedHint}>
          ✓ Status updated to <strong>Contacted</strong> at{' '}
          {format(markedAt, 'h:mm a')}
        </div>
      )}
    </section>
  );
}

export default function EducationInquiries() {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlSearch = searchParams.get('search') || '';
  const status = (searchParams.get('status') || '') as InquiryStatus | '';
  const assignee = searchParams.get('assigned_to') || '';
  const createdAfter = searchParams.get('created_after') || '';
  const createdBefore = searchParams.get('created_before') || '';
  const emailSent = searchParams.get('email_sent') || '';
  const ordering = (searchParams.get('ordering') || '-created_at') as InquiryOrdering;
  const offset = Math.max(0, parseInt(searchParams.get('offset') || '0', 10));

  const [searchInput, setSearchInput] = useState(urlSearch);
  const debouncedSearch = useDebounce(searchInput, 300);

  const [items, setItems] = useState<EducationInquiry[]>([]);
  const [count, setCount] = useState(0);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadIdRef = useRef(0);

  const [activeId, setActiveId] = useState<string | null>(null);

  const setParam = useCallback(
    (updates: Record<string, string | null>, resetOffset = true) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (v === null || v === '') next.delete(k);
            else next.set(k, v);
          }
          if (resetOffset && !('offset' in updates)) next.delete('offset');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const prevSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    if (prevSearchRef.current === debouncedSearch) return;
    prevSearchRef.current = debouncedSearch;
    setParam({ search: debouncedSearch || null });
  }, [debouncedSearch, setParam]);

  const loadInquiries = useCallback(async () => {
    const thisLoad = ++loadIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const filters: InquiryFilters = {
        ordering,
        limit: PAGE_SIZE,
        offset,
      };
      if (debouncedSearch) filters.search = debouncedSearch;
      if (status) filters.status = status;
      if (assignee === 'unassigned') filters.assigned_to = 'unassigned';
      else if (assignee && /^\d+$/.test(assignee)) filters.assigned_to = parseInt(assignee, 10);
      if (createdAfter) filters.created_after = createdAfter;
      if (createdBefore) filters.created_before = createdBefore;
      if (emailSent === 'true') filters.email_sent = true;
      else if (emailSent === 'false') filters.email_sent = false;

      const data = await listInquiries(filters);
      if (thisLoad !== loadIdRef.current) return;
      setItems(data.results);
      setCount(data.count);
      setNextOffset(data.next_offset);
    } catch {
      if (thisLoad !== loadIdRef.current) return;
      setError('Failed to load inquiries');
    } finally {
      if (thisLoad === loadIdRef.current) setIsLoading(false);
    }
  }, [ordering, offset, debouncedSearch, status, assignee, createdAfter, createdBefore, emailSent]);

  useEffect(() => {
    loadInquiries();
  }, [loadInquiries]);

  const knownAssignees = useMemo(() => {
    const map = new Map<number, string>();
    for (const i of items) {
      if (i.assigned_to != null) {
        map.set(i.assigned_to, i.assigned_to_name || i.assigned_to_email || `User #${i.assigned_to}`);
      }
    }
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);

  const activeInquiry = useMemo(
    () => items.find((i) => i.id === activeId) || null,
    [items, activeId],
  );

  const handleInquiryUpdated = useCallback((updated: EducationInquiry) => {
    setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
  }, []);

  const pageStart = offset + (items.length > 0 ? 1 : 0);
  const pageEnd = offset + items.length;
  const hasPrev = offset > 0;
  const hasNext = nextOffset !== null;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Education Inquiries</h1>
          <p className={styles.subtitle}>
            {count.toLocaleString()} total{' '}
            {assignee || status || debouncedSearch || createdAfter || createdBefore || emailSent
              ? '(filtered)'
              : ''}
          </p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.refreshButton} onClick={loadInquiries} disabled={isLoading}>
            <span className={`${styles.refreshIcon} ${isLoading ? styles.spinning : ''}`}>↻</span>
            Refresh
          </button>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.searchWrapper}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search name, email, organization, role, message, notes…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button
              className={styles.clearSearchButton}
              onClick={() => setSearchInput('')}
              title="Clear"
            >
              ✕
            </button>
          )}
        </div>
        <div className={styles.filterGroup}>
          <select
            className={styles.select}
            value={status}
            onChange={(e) => setParam({ status: e.target.value || null })}
          >
            <option value="">All Statuses</option>
            {INQUIRY_STATUSES.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
          <select
            className={styles.select}
            value={assignee}
            onChange={(e) => setParam({ assigned_to: e.target.value || null })}
          >
            <option value="">All Assignees</option>
            <option value="unassigned">Unassigned</option>
            {knownAssignees.map(([id, name]) => (
              <option key={id} value={String(id)}>
                {name}
              </option>
            ))}
          </select>
          <select
            className={styles.select}
            value={emailSent}
            onChange={(e) => setParam({ email_sent: e.target.value || null })}
          >
            <option value="">Email: Any</option>
            <option value="true">Notification sent</option>
            <option value="false">Notification failed</option>
          </select>
          <input
            type="date"
            className={styles.dateInput}
            value={createdAfter ? createdAfter.slice(0, 10) : ''}
            onChange={(e) =>
              setParam({
                created_after: e.target.value ? `${e.target.value}T00:00:00Z` : null,
              })
            }
            title="Created after"
          />
          <input
            type="date"
            className={styles.dateInput}
            value={createdBefore ? createdBefore.slice(0, 10) : ''}
            onChange={(e) =>
              setParam({
                created_before: e.target.value ? `${e.target.value}T23:59:59Z` : null,
              })
            }
            title="Created before"
          />
          <select
            className={styles.select}
            value={ordering}
            onChange={(e) => setParam({ ordering: e.target.value })}
          >
            {ORDERING_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          {error}
          <button className={styles.retryButton} onClick={loadInquiries}>
            Retry
          </button>
        </div>
      )}

      <div className={styles.tableContainer}>
        {isLoading && items.length === 0 ? (
          <div className={styles.loading}>Loading inquiries…</div>
        ) : items.length === 0 ? (
          <div className={styles.emptyState}>
            No inquiries found{debouncedSearch || status || assignee ? ' for these filters' : ' yet'}.
          </div>
        ) : (
          <>
            <table className={`${styles.table} ${isLoading ? styles.tableLoading : ''}`}>
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Organization</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Qty</th>
                  <th>Intended Use</th>
                  <th>Timeline</th>
                  <th>Assignee</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr
                    key={i.id}
                    className={`${styles.row} ${activeId === i.id ? styles.rowActive : ''}`}
                    onClick={() => setActiveId(i.id)}
                  >
                    <td>
                      <div className={styles.timestamp}>
                        <span className={styles.timestampRel} title={safeFullDate(i.created_at)}>
                          {safeRelative(i.created_at)}
                        </span>
                        <span className={styles.timestampFull}>
                          {format(parseISO(i.created_at), 'MMM d, yyyy')}
                        </span>
                      </div>
                    </td>
                    <td className={styles.orgCell}>{i.organization || '—'}</td>
                    <td className={styles.nameCell}>{i.name || '—'}</td>
                    <td className={styles.emailCell}>
                      <a
                        href={`mailto:${i.email}`}
                        className={styles.emailLink}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {i.email}
                      </a>
                      <CopyEmailButton email={i.email} />
                      {!i.email_sent_successfully && (
                        <span className={styles.emailWarn} title={i.email_send_error || 'Notification email failed to send'}>
                          ⚠
                        </span>
                      )}
                    </td>
                    <td>
                      <div className={styles.statusCell}>
                        <StatusBadge status={i.status} />
                        {i.status === 'new' && (
                          <MarkContactedButton inquiry={i} onUpdated={handleInquiryUpdated} />
                        )}
                      </div>
                    </td>
                    <td className={styles.qtyCell}>
                      {i.estimated_quantity != null ? i.estimated_quantity.toLocaleString() : '—'}
                    </td>
                    <td className={styles.useCell}>{i.intended_use || '—'}</td>
                    <td className={styles.timelineCell}>{i.timeline || '—'}</td>
                    <td>
                      {i.assigned_to_name ? (
                        <span className={styles.assigneeChip} title={i.assigned_to_name}>
                          <span className={styles.assigneeAvatar}>{initials(i.assigned_to_name)}</span>
                          <span className={styles.assigneeName}>{i.assigned_to_name}</span>
                        </span>
                      ) : (
                        <span className={styles.unassigned}>Unassigned</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className={styles.mobileCards}>
              {items.map((i) => (
                <div
                  key={i.id}
                  className={`${styles.mobileCard} ${activeId === i.id ? styles.rowActive : ''}`}
                  onClick={() => setActiveId(i.id)}
                >
                  <div className={styles.mobileCardHeader}>
                    <span className={styles.orgMobile}>{i.organization || '—'}</span>
                    <div className={styles.statusCell}>
                      <StatusBadge status={i.status} />
                      {i.status === 'new' && (
                        <MarkContactedButton inquiry={i} onUpdated={handleInquiryUpdated} />
                      )}
                    </div>
                  </div>
                  <div className={styles.mobileCardBody}>
                    <span>{i.name}</span>
                    <span className={styles.emailMobile}>
                      {i.email}
                      <CopyEmailButton email={i.email} />
                    </span>
                    <span>
                      {i.estimated_quantity != null ? `${i.estimated_quantity} units` : 'Qty —'}
                    </span>
                    <span>{safeRelative(i.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {(hasPrev || hasNext || items.length > 0) && (
        <div className={styles.pagination}>
          <span className={styles.pageInfo}>
            {items.length > 0
              ? `${pageStart.toLocaleString()}–${pageEnd.toLocaleString()} of ${count.toLocaleString()}`
              : '—'}
          </span>
          <div className={styles.pageButtons}>
            <button
              className={styles.pageButton}
              disabled={!hasPrev || isLoading}
              onClick={() => setParam({ offset: String(Math.max(0, offset - PAGE_SIZE)) }, false)}
            >
              ← Previous
            </button>
            <button
              className={styles.pageButton}
              disabled={!hasNext || isLoading}
              onClick={() => nextOffset != null && setParam({ offset: String(nextOffset) }, false)}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {activeInquiry && (
        <InquiryDrawer
          inquiry={activeInquiry}
          onClose={() => setActiveId(null)}
          onUpdated={handleInquiryUpdated}
          knownAssignees={knownAssignees}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drawer
// ---------------------------------------------------------------------------

interface DrawerProps {
  inquiry: EducationInquiry;
  onClose: () => void;
  onUpdated: (i: EducationInquiry) => void;
  knownAssignees: [number, string][];
}

function InquiryDrawer({ inquiry, onClose, onUpdated, knownAssignees }: DrawerProps) {
  const [statusVal, setStatusVal] = useState<InquiryStatus>(inquiry.status);
  const [assigneeVal, setAssigneeVal] = useState<string>(
    inquiry.assigned_to != null ? String(inquiry.assigned_to) : '',
  );
  const [notes, setNotes] = useState<string>(inquiry.internal_notes || '');
  const [contactedAt, setContactedAt] = useState<string>(toInputDateTime(inquiry.contacted_at));
  const [convertedAt, setConvertedAt] = useState<string>(toInputDateTime(inquiry.converted_at));
  const [conversionValue, setConversionValue] = useState<string>(inquiry.conversion_value || '');
  const [lostReason, setLostReason] = useState<string>(inquiry.lost_reason || '');
  const [estimatedQty, setEstimatedQty] = useState<string>(
    inquiry.estimated_quantity != null ? String(inquiry.estimated_quantity) : '',
  );

  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Reset form when active inquiry changes (different row clicked while drawer open)
  useEffect(() => {
    setStatusVal(inquiry.status);
    setAssigneeVal(inquiry.assigned_to != null ? String(inquiry.assigned_to) : '');
    setNotes(inquiry.internal_notes || '');
    setContactedAt(toInputDateTime(inquiry.contacted_at));
    setConvertedAt(toInputDateTime(inquiry.converted_at));
    setConversionValue(inquiry.conversion_value || '');
    setLostReason(inquiry.lost_reason || '');
    setEstimatedQty(inquiry.estimated_quantity != null ? String(inquiry.estimated_quantity) : '');
    setFieldError(null);
    setSavedAt(null);
  }, [inquiry]);

  // Effective quantity used by the email-template builder. Reflects the
  // staged (unsaved) value so price tier and teacher-device defaults react
  // live as the user types.
  const effectiveQuantity = useMemo<number | null>(() => {
    const trimmed = estimatedQty.trim();
    if (trimmed === '') return null;
    const n = Number(trimmed);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
  }, [estimatedQty]);

  // Esc closes drawer
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const dirty =
    statusVal !== inquiry.status ||
    assigneeVal !== (inquiry.assigned_to != null ? String(inquiry.assigned_to) : '') ||
    notes !== (inquiry.internal_notes || '') ||
    contactedAt !== toInputDateTime(inquiry.contacted_at) ||
    convertedAt !== toInputDateTime(inquiry.converted_at) ||
    conversionValue !== (inquiry.conversion_value || '') ||
    lostReason !== (inquiry.lost_reason || '') ||
    estimatedQty !== (inquiry.estimated_quantity != null ? String(inquiry.estimated_quantity) : '');

  const handleSave = async () => {
    setSaving(true);
    setFieldError(null);
    try {
      const payload: InquiryPatchPayload = {};
      if (statusVal !== inquiry.status) payload.status = statusVal;
      if (assigneeVal !== (inquiry.assigned_to != null ? String(inquiry.assigned_to) : '')) {
        payload.assigned_to = assigneeVal === '' ? null : parseInt(assigneeVal, 10);
      }
      if (notes !== (inquiry.internal_notes || '')) payload.internal_notes = notes;
      if (contactedAt !== toInputDateTime(inquiry.contacted_at)) {
        payload.contacted_at = fromInputDateTime(contactedAt);
      }
      if (convertedAt !== toInputDateTime(inquiry.converted_at)) {
        payload.converted_at = fromInputDateTime(convertedAt);
      }
      if (conversionValue !== (inquiry.conversion_value || '')) {
        payload.conversion_value = conversionValue.trim() === '' ? null : conversionValue.trim();
      }
      if (lostReason !== (inquiry.lost_reason || '')) payload.lost_reason = lostReason;
      if (
        estimatedQty !==
        (inquiry.estimated_quantity != null ? String(inquiry.estimated_quantity) : '')
      ) {
        const trimmed = estimatedQty.trim();
        if (trimmed === '') {
          payload.estimated_quantity = null;
        } else {
          const parsed = Number(trimmed);
          if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed <= 0) {
            setFieldError('Estimated quantity must be a positive whole number.');
            setSaving(false);
            return;
          }
          payload.estimated_quantity = parsed;
        }
      }

      const updated = await patchInquiry(inquiry.id, payload);
      onUpdated(updated);
      setSavedAt(new Date());
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to save changes';
      setFieldError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className={styles.drawerScrim} onClick={onClose} />
      <aside className={styles.drawer} role="dialog" aria-label="Inquiry detail">
        <div className={styles.drawerHeader}>
          <div className={styles.drawerHeaderText}>
            <h2 className={styles.drawerTitle}>{inquiry.organization || inquiry.name}</h2>
            <p className={styles.drawerSubtitle}>
              <span>{inquiry.name} · </span>
              <a
                href={`mailto:${inquiry.email}`}
                className={styles.drawerSubtitleEmail}
              >
                {inquiry.email}
              </a>
              <CopyEmailButton email={inquiry.email} />
            </p>
          </div>
          <button className={styles.drawerClose} onClick={onClose} title="Close (Esc)">
            ✕
          </button>
        </div>

        <div className={styles.drawerBody}>
          {/* Submission - read only */}
          <section className={styles.drawerSection}>
            <h3 className={styles.drawerSectionTitle}>Submission</h3>
            <dl className={styles.kv}>
              <div>
                <dt>Submitted</dt>
                <dd>{safeFullDate(inquiry.created_at)}</dd>
              </div>
              <div>
                <dt>Role</dt>
                <dd>{inquiry.role || '—'}</dd>
              </div>
              <div>
                <dt>Phone</dt>
                <dd>{inquiry.phone || '—'}</dd>
              </div>
              <div>
                <dt>Intended Use</dt>
                <dd>{inquiry.intended_use || '—'}</dd>
              </div>
              <div>
                <dt>Timeline</dt>
                <dd>{inquiry.timeline || '—'}</dd>
              </div>
              <div>
                <dt>Notification Email</dt>
                <dd>
                  {inquiry.email_sent_successfully ? (
                    <span className={styles.kvOk}>Delivered</span>
                  ) : (
                    <span className={styles.kvFail} title={inquiry.email_send_error}>
                      Failed{inquiry.email_send_error ? `: ${inquiry.email_send_error}` : ''}
                    </span>
                  )}
                </dd>
              </div>
            </dl>

            <h4 className={styles.drawerSubTitle}>Message</h4>
            <div className={styles.message}>
              {inquiry.message ? (
                inquiry.message.split(/\n+/).map((p, idx) => <p key={idx}>{p}</p>)
              ) : (
                <p className={styles.muted}>No message provided.</p>
              )}
            </div>
          </section>

          {/* CRM - editable */}
          <section className={styles.drawerSection}>
            <div className={styles.crmHeader}>
              <h3 className={styles.drawerSectionTitle}>CRM</h3>
              {savedAt && !dirty && (
                <span className={styles.savedHint}>Saved {format(savedAt, 'h:mm:ss a')}</span>
              )}
            </div>

            {fieldError && <div className={styles.fieldError}>{fieldError}</div>}

            <div className={styles.field}>
              <label>Status</label>
              <select
                value={statusVal}
                onChange={(e) => setStatusVal(e.target.value as InquiryStatus)}
                className={styles.input}
              >
                {INQUIRY_STATUSES.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.field}>
              <label>Assignee</label>
              <select
                value={assigneeVal}
                onChange={(e) => setAssigneeVal(e.target.value)}
                className={styles.input}
              >
                <option value="">Unassigned</option>
                {knownAssignees.map(([id, name]) => (
                  <option key={id} value={String(id)}>
                    {name}
                  </option>
                ))}
                {/* Preserve current assignee even if not in known list (e.g., new staff) */}
                {inquiry.assigned_to != null &&
                  !knownAssignees.find(([id]) => id === inquiry.assigned_to) && (
                    <option value={String(inquiry.assigned_to)}>
                      {inquiry.assigned_to_name || `User #${inquiry.assigned_to}`}
                    </option>
                  )}
              </select>
              <span className={styles.fieldHint}>
                Only staff users can be assigned. Backend rejects non-staff with 400.
              </span>
            </div>

            <div className={styles.field}>
              <label>Estimated Quantity</label>
              <input
                type="number"
                inputMode="numeric"
                min="1"
                step="1"
                placeholder={
                  inquiry.estimated_quantity != null
                    ? String(inquiry.estimated_quantity)
                    : 'e.g. 30'
                }
                value={estimatedQty}
                onChange={(e) => setEstimatedQty(e.target.value)}
                className={`${styles.input} ${styles.priceInput}`}
              />
              <span className={styles.fieldHint}>
                {inquiry.estimated_quantity == null
                  ? 'Educator left this blank — enter a staff estimate to drive pricing & analytics.'
                  : 'Editable. Used for tier price in the email template and for analytics rollups.'}
              </span>
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label>Contacted At</label>
                <input
                  type="datetime-local"
                  value={contactedAt}
                  onChange={(e) => setContactedAt(e.target.value)}
                  className={styles.input}
                />
              </div>
              <div className={styles.field}>
                <label>Converted At</label>
                <input
                  type="datetime-local"
                  value={convertedAt}
                  onChange={(e) => setConvertedAt(e.target.value)}
                  className={styles.input}
                />
              </div>
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label>Conversion Value (USD)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="e.g. 1234.56"
                  value={conversionValue}
                  onChange={(e) => setConversionValue(e.target.value)}
                  className={styles.input}
                />
              </div>
              <div className={styles.field}>
                <label>Lost Reason</label>
                <input
                  type="text"
                  maxLength={255}
                  placeholder="Only used when status = lost"
                  value={lostReason}
                  onChange={(e) => setLostReason(e.target.value)}
                  className={styles.input}
                />
              </div>
            </div>

            <div className={styles.field}>
              <label>Internal Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={5}
                className={styles.textarea}
              />
            </div>

            <div className={styles.drawerActions}>
              <button
                className={styles.saveButton}
                disabled={!dirty || saving}
                onClick={handleSave}
              >
                {saving ? 'Saving…' : dirty ? 'Save changes' : 'No changes'}
              </button>
              <button className={styles.cancelButton} onClick={onClose}>
                Close
              </button>
            </div>
          </section>

          <SuggestedResponseSection
            inquiry={inquiry}
            onUpdated={onUpdated}
            effectiveQuantity={effectiveQuantity}
          />
        </div>
      </aside>
    </>
  );
}
